/**
 * Re 1 SheetDB forms — shared validation, submit, confetti (collection + product).
 * Selectors: [data-sheetdb-form], [data-re-1-notify-form]
 */
(function () {
  'use strict';

  const PHONE_ERROR =
    'Enter your complete 10-digit WhatsApp number (e.g. 9876543210 or +919876543210).';

  function parseCompleteIndianPhone(raw) {
    const trimmed = String(raw).trim();
    if (!trimmed) return null;

    let digits = trimmed.replace(/\D/g, '');
    if (digits.length === 0) return null;

    if (digits.length === 12 && digits.startsWith('91')) {
      digits = digits.slice(2);
    } else if (digits.length === 11 && digits.startsWith('0')) {
      digits = digits.slice(1);
    }

    if (digits.length !== 10 || !/^[6-9]\d{9}$/.test(digits)) {
      return null;
    }

    return '+91' + digits;
  }

  function isValidName(value) {
    return String(value).trim().length >= 2;
  }

  function getFieldErrorEl(input) {
    if (!input) return null;
    const id = input.getAttribute('aria-describedby');
    return id ? document.getElementById(id) : null;
  }

  function showFieldError(input, message) {
    if (!input) return;
    const errorEl = getFieldErrorEl(input);
    input.setAttribute('aria-invalid', 'true');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  function clearFieldError(input) {
    if (!input) return;
    const errorEl = getFieldErrorEl(input);
    input.removeAttribute('aria-invalid');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  function hideFormMessage(element) {
    if (!element) return;
    element.hidden = true;
    element.textContent = '';
  }

  function showFormMessage(element, message) {
    if (!element) return;
    element.textContent = message;
    element.hidden = false;
  }

  function validatePhoneInput(phoneInput) {
    const raw = phoneInput?.value.trim() || '';
    if (!raw) {
      showFieldError(phoneInput, PHONE_ERROR);
      return null;
    }
    const normalized = parseCompleteIndianPhone(raw);
    if (!normalized) {
      showFieldError(phoneInput, PHONE_ERROR);
      return null;
    }
    clearFieldError(phoneInput);
    return normalized;
  }

  function maybeConfetti(form) {
    if (form.dataset.re1Confetti === 'false') return;
    if (window.Re1Confetti && typeof window.Re1Confetti.fire === 'function') {
      window.Re1Confetti.fire();
    }
  }

  async function submitForm(form) {
    const apiUrl = form.dataset.sheetdbUrl;
    const nameColumn = form.dataset.sheetdbNameColumn || 'NAME';
    const phoneColumn = form.dataset.sheetdbPhoneColumn || 'WHATSAPP';
    const createdColumn = form.dataset.sheetdbCreatedColumn;

    const nameInput = form.querySelector('[data-field-name]');
    const phoneInput = form.querySelector('[data-field-phone]');
    const submitButton = form.querySelector('[data-form-submit]');
    const submitText = form.querySelector('[data-form-submit-text]');
    const successMessage = form.querySelector('[data-form-success]');
    const errorMessage = form.querySelector('[data-form-error]');

    const successCopy =
      form.dataset.formSuccessMessage ||
      "You're on the list! We'll WhatsApp you before the next sale.";
    const errorCopy =
      form.dataset.formErrorMessage || 'Something went wrong. Please try again.';

    hideFormMessage(successMessage);
    hideFormMessage(errorMessage);
    clearFieldError(nameInput);
    clearFieldError(phoneInput);

    if (!apiUrl) {
      showFormMessage(errorMessage, errorCopy);
      return;
    }

    const name = nameInput?.value.trim() || '';
    let valid = true;

    if (!isValidName(name)) {
      showFieldError(nameInput, 'Please enter your name (at least 2 characters).');
      valid = false;
    }

    const phoneNormalized = validatePhoneInput(phoneInput);
    if (!phoneNormalized) {
      valid = false;
    }

    if (!valid) {
      showFormMessage(errorMessage, 'Please fix the errors above.');
      return;
    }

    const row = {
      [nameColumn]: name,
      [phoneColumn]: phoneNormalized,
    };

    if (createdColumn) {
      row[createdColumn] = 'DATETIME';
    }

    const defaultSubmitLabel = submitText?.textContent || 'Notify Me';

    if (submitButton) submitButton.disabled = true;
    if (submitText) submitText.textContent = 'Sending...';

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: [row] }),
      });

      if (!response.ok) {
        throw new Error(`SheetDB request failed (${response.status})`);
      }

      showFormMessage(successMessage, successCopy);
      maybeConfetti(form);
      form.reset();
    } catch (error) {
      console.error('Re 1 SheetDB signup error:', error);
      showFormMessage(errorMessage, errorCopy);
    } finally {
      if (submitButton) submitButton.disabled = false;
      if (submitText) submitText.textContent = defaultSubmitLabel;
    }
  }

  function bindForm(form) {
    if (form.dataset.re1SheetdbBound === 'true') return;
    form.dataset.re1SheetdbBound = 'true';

    const nameInput = form.querySelector('[data-field-name]');
    const phoneInput = form.querySelector('[data-field-phone]');

    if (nameInput) {
      nameInput.addEventListener('input', () => clearFieldError(nameInput));
      nameInput.addEventListener('blur', () => {
        if (nameInput.value.trim() && !isValidName(nameInput.value)) {
          showFieldError(nameInput, 'Please enter your name (at least 2 characters).');
        }
      });
    }

    if (phoneInput) {
      phoneInput.addEventListener('input', () => {
        const digits = phoneInput.value.replace(/\D/g, '');
        if (digits.length > 12) {
          phoneInput.value = phoneInput.value.slice(0, -1);
        }
        if (parseCompleteIndianPhone(phoneInput.value)) {
          clearFieldError(phoneInput);
        }
      });

      phoneInput.addEventListener('blur', () => {
        if (phoneInput.value.trim()) {
          validatePhoneInput(phoneInput);
        }
      });
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submitForm(form);
    });
  }

  function boot(scope) {
    const rootScope = scope || document;
    rootScope
      .querySelectorAll('[data-sheetdb-form], [data-re-1-notify-form]')
      .forEach(bindForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
  });

  window.Re1SheetdbForm = {
    parseCompleteIndianPhone,
    isValidName,
  };
})();
